import { IWizValidatorDirective } from './../../interfaces/iwiz-error';
import { WizErrorManager } from './../../services/core/errors/wiz-error.manager';
import { WizValidationErrors } from './../../services/validators/wiz-validators';
import { BASE_COMPONENT } from './../../components/core/wiz-base-component';
import { Directive, ElementRef, Self, Inject, Input, ViewContainerRef, AfterContentInit, OnDestroy } from '@angular/core';
import { WizBaseComponent } from '../../components/core/wiz-base-component';
import { WizValidatorFn } from '../../services/validators/wiz-validators';
import { printErrors } from '../../utils/wiz-validation.utils';

/**
 * Directive to indicate that the component can be validated
 *
 * User must call check method on the parent container directive to get the result errors
 */
@Directive({
  selector: '[wizValidator]'
})
export class WizValidatorDirective implements IWizValidatorDirective, AfterContentInit, OnDestroy {

  /**
   * Stores the last resulting errors
   */
  private _lastErrors: WizValidationErrors;

  /**
   * An additionnal validation function that will be called updon validation.
   *
   * This is intended to provide business case validation process on a particular component
   */
  @Input() public wizValidator: WizValidatorFn;

  /**
   * Indicates wether or not the component must show the errors in its very own error container
   */
  @Input() public wizShowErrors: boolean;

  constructor(public elementRef: ElementRef, @Inject(BASE_COMPONENT) @Self() public sourceComponent: WizBaseComponent) {
    // This directive does not work if the component provided is not a WizBaseComponent
    if (!sourceComponent) {
      throw new Error('A wizValidators directive can only be put on a WizBaseComponent');
    }
  }

  ngAfterContentInit() {
    // Registers the component and the validation context in the focus manager
    WizErrorManager.instance.registerValidationContext(this.sourceComponent, this);
  }

  ngOnDestroy() {
    // Unregisters the component when it is destroyed
    WizErrorManager.instance.unregisterValidationContext(this.sourceComponent);
  }

  /**
   * Executes the validators for this component.
   *
   * It will execute both the given validator and the validators provided by the component if they are any.
   *
   * @param pShowErrors Indicates if must be shown in the container
   * @param pErrorContainer Shows all the errors in the container given rather than the component error container
   * @param pNumResults Number of results to show in the container. Default -1 to show all results
   * @param pClear Indicates if we must clear the container before showing results
   */
  public check( pShowErrors: boolean,
                pDeep: boolean,
                pErrorContainer: ViewContainerRef,
                pNumResults: number,
                pClear: boolean): WizValidationErrors {
    let result = [];
    // List of validators to execute on this component
    let validators = !!this.wizValidator ? [this.wizValidator] : [];
    validators = validators.concat(this.sourceComponent.getValidators());

    if (validators.length > 0) {
      // Executes each validator and concatenates the resulting errors in an array
      validators.forEach(validator => {
        const validationResult: WizValidationErrors = validator(this.sourceComponent);
        if (validationResult && validationResult.length > 0) {
          result = result.concat(validationResult);
        }
      });
    }

    // Should we show errors?
    let showErrors: boolean = pShowErrors !== undefined ? pShowErrors : this.wizShowErrors;
    showErrors = showErrors === undefined ? WizErrorManager.DEFAULT_SHOW_ERRORS : showErrors;

    // If we've got some errors and the user wants to show them, we add them to the right container
    if (showErrors) {
      pErrorContainer = pErrorContainer ? pErrorContainer : this.sourceComponent.getErrorVCRef();
      // console.log('printErrors');
      printErrors(result, pErrorContainer, pNumResults, pClear);
    }


    // Tells the component that is has been validated
    this.sourceComponent.onWizValidation(result, showErrors);

    // Keep track of resulting validation errors
    this._lastErrors = result;

    // Return the result no matter what
    return result;
  }
}
